import {reaction, runInAction} from 'mobx';
import {subscribeAuthTokenCommit} from '../http/siteAuthTokenCommit';
import {BASKET_RECOVERY_ERROR, BASKET_SNAPSHOT_ERROR, basketIdentityItems} from '../utils/siteBasketSnapshot';

const principalId = id => Number.isSafeInteger(id) && id > 0 && id <= 2147483647;
const discarded = () => ({status: 'discarded'});

// Internal lifetime coordinator, not an authentication provider or a stock grant.
export const createBasketSessionCoordinator = main => {
  const {user, basket} = main;
  let disposed = false, generation = 0, activeAttempt = null, settled = null;
  let activeTokenCommitted = false, activeCredential = null;
  let publicationRequired = null, guestProof = null, guestProofOwner = null, mergePending = false, refreshing = null;
  basket.lockSession();

  const isCurrent = attempt => !disposed && attempt === activeAttempt && attempt !== null;
  const captureGuest = () => {
    if (basket._session?.authScope === 'guest' && basket.recoveryProvenance === 'guest' && !basket.recoveryError) {
      // Current acknowledged/session-local guest intent can change after its last
      // refresh (legacy add/remove). Never resurrect that older recovery snapshot.
      // Pending/error is a grant barrier, not a reason to undo already-owned
      // legacy intent. UNKNOWN has no acknowledged live intent and uses recovery.
      const lines = basket.snapshotMode === 'legacy' ? basket.basketItems : basket.recoverableIdentities;
      guestProofOwner = null;
      try {guestProof = basketIdentityItems(lines).map(line => Object.freeze({...line}));}
      catch {guestProof = null;}
    }
  };
  const invalidate = () => {settled = null; refreshing = null; basket.lockSession();};

  const refresh = () => {
    if (disposed || !settled || !basket._session) return Promise.resolve(discarded());
    const epoch = basket.sessionEpoch;
    if (refreshing?.epoch === epoch) return refreshing.promise;
    const ticket = {epoch, promise: null};
    const mergeGuestIdentities = settled.authScope === 'authenticated' && mergePending;
    ticket.promise = basket.queueSessionRefresh({mergeGuestIdentities}).then(result => {
      if (disposed || basket.sessionEpoch !== epoch) return discarded();
      if (result.status === 'accepted') {
        if (mergeGuestIdentities) {mergePending = false; guestProof = null; guestProofOwner = null;}
        return {status: 'accepted'};
      }
      if (result.status === 'failed') return {status: 'failed', error:
        result.error === BASKET_RECOVERY_ERROR ? BASKET_RECOVERY_ERROR : BASKET_SNAPSHOT_ERROR};
      return discarded();
    }).finally(() => {if (refreshing === ticket) refreshing = null;});
    refreshing = ticket;
    return ticket.promise;
  };

  const settle = identity => {
    if (settled?.authScope === identity.authScope && settled.principalId === identity.principalId && basket._session) return;
    // Pin intent at the first ACCEPTED principal, before any merge dispatch.
    // Pending/failed same-owner retries may recover it; another owner cannot.
    if (identity.authScope === 'authenticated' && guestProof !== null) {
      if (guestProofOwner === null) guestProofOwner = identity.principalId;
      else if (guestProofOwner !== identity.principalId) {guestProof = null; guestProofOwner = null;}
    }
    settled = identity; publicationRequired = null; refreshing = null;
    basket.beginSession(identity);
    mergePending = identity.authScope === 'authenticated' && guestProof !== null;
    if (mergePending) {
      basket.recoverableIdentities = guestProof.map(line => ({...line}));
      basket.recoveryProvenance = 'guest'; basket.recoveryError = null;
    }
    refresh();
  };

  const observePrincipal = () => {
    if (disposed || activeAttempt) return;
    // A before-write event still sees the old coherent tuple. It cannot authorize
    // those credentials until a genuinely subsequent User publication occurs.
    if (publicationRequired !== null && user.user === publicationRequired) return;
    if (user.isAuth === true && principalId(user.user?.id)) settle({authScope: 'authenticated', principalId: user.user.id});
    else if (!(settled?.authScope === 'guest' && user.isAuth === false && user.user?.id == null)) invalidate();
  };
  const stopObserver = reaction(() => [user.isAuth, user.user?.id, user.user], observePrincipal);
  const stopCredential = subscribeAuthTokenCommit(event => {
    if (disposed) return;
    const tracked = isCurrent(event.commitContext);
    if (tracked) activeTokenCommitted = true;
    if (!tracked) {activeAttempt = null; guestProof = null; guestProofOwner = null; mergePending = false;}
    publicationRequired = user.user;
    // Deliberately synchronous, including when the caller is inside a MobX action.
    invalidate();
  });

  const start = kind => {
    if (disposed) return Object.freeze({generation: -1, kind});
    captureGuest(); invalidate(); publicationRequired = user.user;
    const credential = localStorage.getItem('token');
    const ticket = Object.freeze({generation: ++generation, kind, hadCredential: Boolean(credential)});
    activeAttempt = ticket;
    activeCredential = kind === 'bootstrap' ? credential : null;
    activeTokenCommitted = false;
    return ticket;
  };
  const failAuth = attempt => {
    if (!isCurrent(attempt)) return false;
    activeAttempt = null; publicationRequired = user.user; invalidate();
    basket.snapshotError = BASKET_SNAPSHOT_ERROR;
    return true;
  };
  return {
    startBootstrap: () => start('bootstrap'), startLoginAttempt: () => start('login'),
    isCurrent, canCommitToken: isCurrent,
    acceptAuth: (attempt, decodedUser) => {
      if (!isCurrent(attempt) || !principalId(decodedUser?.id)) return false;
      activeAttempt = null;
      runInAction(() => {
        user.setUser(decodedUser); user.setIsAuth(true);
        settle({authScope: 'authenticated', principalId: decodedUser.id});
      });
      return true;
    },
    acceptGuest: attempt => {
      if (!isCurrent(attempt) || attempt.kind !== 'bootstrap' || attempt.hadCredential ||
        activeTokenCommitted || user.isAuth !== false || user.user?.id != null) return false;
      activeAttempt = null; guestProof = null; guestProofOwner = null;
      settle({authScope: 'guest', principalId: null});
      return true;
    },
    rejectSavedCredential: attempt => {
      if (!isCurrent(attempt) || attempt.kind !== 'bootstrap' || !attempt.hadCredential ||
        activeTokenCommitted || localStorage.getItem('token') !== activeCredential) return false;
      activeAttempt = null; activeCredential = null;
      guestProof = null; guestProofOwner = null; mergePending = false; invalidate();
      localStorage.removeItem('token');
      runInAction(() => {
        user.setUser({}); user.setIsAuth(false); user.resetModalState();
        settle({authScope: 'guest', principalId: null});
      });
      return true;
    },
    failAuth,
    cancelAttempt: attempt => {
      if (!isCurrent(attempt)) return false;
      activeAttempt = null; publicationRequired = user.user; invalidate();
      return true;
    },
    logout: () => {
      if (disposed) return false;
      activeAttempt = null; guestProof = null; guestProofOwner = null; mergePending = false; invalidate();
      localStorage.removeItem('token');
      runInAction(() => {
        user.setUser({}); user.setIsAuth(false); user.resetModalState();
        settle({authScope: 'guest', principalId: null});
      });
      return true;
    },
    refresh,
    dispose: () => {
      if (disposed) return;
      disposed = true; activeAttempt = null; guestProof = null; guestProofOwner = null; mergePending = false;
      stopObserver(); stopCredential(); invalidate();
    },
  };
};
